import React, { useState, useEffect } from 'react';
import { Checkbox, TextField, Button, Typography, Box, CircularProgress, FormControlLabel, Dialog, DialogContent, IconButton } from '@mui/material';
import { CheckCircleSharp as CheckCircleSharpIcon, CloudUpload as CloudUploadIcon, Image as ImageIcon, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon, Close as CloseIcon, Fullscreen as FullscreenIcon } from '@mui/icons-material';
import { getS3PresignedUrl } from 'lib/axiosUtils';
import { HTTP_HEADER_KEYS, HTTP_CONTENT_TYPES } from 'assets/constants/apiConstants';
import ShowToast from 'components/ShowToast';
import { TYPE_SUCCESS, TYPE_FAIL, INTERVAL } from 'assets/constants/Constants';

const FeasibilityStudyCard = ({ study, index, onChange }) => {
    const safeStudy = study || {};
    const [localParts, setLocalParts] = useState(safeStudy.partsTable || []);
    const [isUploading, setIsUploading] = useState(false);
    const [localImageUrl, setLocalImageUrl] = useState(safeStudy.schematic_image_url || '');
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [localAllRequirementsFulfilled, setLocalAllRequirementsFulfilled] = useState(safeStudy.allRequirementsFulfilled || false);
    const [localIsComplete, setLocalIsComplete] = useState(safeStudy.isComplete || false);

    const handleOpenModal = () => {
        setIsImageModalOpen(true);
        setZoomLevel(1);
    };

    const handleCloseModal = () => {
        setIsImageModalOpen(false);
    };

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.5, 5));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
    };

    const handleImageUpload = async (e) => {
        const fileInput = e.target;
        const file = fileInput.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const fileType = file.type || 'image/jpeg';
            const presignedRes = await getS3PresignedUrl({}, {
                fileName: file.name,
                fileType: fileType
            });
            const putUrl = presignedRes?.data?.url;
            const publicUrl = presignedRes?.data?.fileUrl;

            if (!putUrl) {
                throw new Error("Did not receive a valid upload URL from the server.");
            }

            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('PUT', putUrl, true);
                xhr.setRequestHeader(HTTP_HEADER_KEYS.CONTENT_TYPE, fileType);
                xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed with status ${xhr.status}`)));
                xhr.onerror = () => reject(new Error("Network error during upload"));
                xhr.send(file);
            });
            
            setLocalImageUrl(publicUrl);
            onChange(index, { target: { name: 'schematic_image_url', value: publicUrl, type: 'text' } });
            ShowToast('Schematic image uploaded successfully!', TYPE_SUCCESS, INTERVAL);
        } catch (error) {
            console.error("Failed to upload schematic image", error);
            ShowToast(error?.message || 'Failed to upload schematic image', TYPE_FAIL, INTERVAL);
        } finally {
            setIsUploading(false);
            if (fileInput) fileInput.value = '';
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isUploading) return;
        
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/')) {
            const syntheticEvent = { target: { files: [file], value: '' } };
            handleImageUpload(syntheticEvent);
        } else if (file) {
            ShowToast('Please upload an image file.', TYPE_FAIL, INTERVAL);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    useEffect(() => {
        setLocalParts(safeStudy.partsTable || []);
    }, [safeStudy.partsTable]);

    useEffect(() => {
        setLocalImageUrl(safeStudy.schematic_image_url || '');
    }, [safeStudy.schematic_image_url]);

    useEffect(() => {
        setLocalAllRequirementsFulfilled(safeStudy.allRequirementsFulfilled || false);
    }, [safeStudy.allRequirementsFulfilled]);

    useEffect(() => {
        setLocalIsComplete(safeStudy.isComplete || false);
    }, [safeStudy.isComplete]);

    const handleLocalRowChange = (rowIndex, field, value) => {
        const updatedParts = [...localParts];
        updatedParts[rowIndex] = { ...updatedParts[rowIndex], [field]: value };
        setLocalParts(updatedParts);
    };

    const pushRowChange = (rowIndex, field, value) => {
        const updatedParts = [...localParts];
        updatedParts[rowIndex] = { ...updatedParts[rowIndex], [field]: value };
        // Push update to parent state
        onChange(index, { target: { name: 'partsTable', value: updatedParts, type: 'array' } });
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        if (name === 'allRequirementsFulfilled') {
            setLocalAllRequirementsFulfilled(checked);
        } else if (name === 'isComplete') {
            setLocalIsComplete(checked);
        }
        onChange(index, { target: { name, checked, type: 'checkbox' } });
    };

    return (
        <div className="bom-page feasibility-card" style={{ padding: '28px', background: '#fff', borderRadius: '12px', border: '1px solid #eaeaea', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
            
            <div className="bom-table-title" style={{ marginBottom: '24px', fontSize: '18px' }}>
                Fixture Name: <span style={{ fontWeight: 400, marginLeft: '8px', color: '#555' }}>{safeStudy.fixture_name || 'Not Configured'}</span>
            </div>

            <div className="table-responsive mb-4" style={{ paddingLeft: '15px', paddingRight: '15px' }}>
                {localParts.length === 0 ? (
                    <Box sx={{ backgroundColor: '#fafafa', border: '1px dashed #d0d0d0', borderRadius: '8px', padding: '40px 20px', textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ color: '#666', fontSize: '14px' }}>
                            No configuration selected. Please add a part with a specific Component and Fixture Type.
                        </Typography>
                    </Box>
                ) : (
                    Array.from(new Set(localParts.map(p => p.category))).map((categoryName, tableIdx) => (
                        <Box key={tableIdx} mb={4}>
                            <Typography className="bom-dialog-section-header" sx={{ mb: 2, pb: 1, color: '#2d3d42', fontSize: '15px !important' }}>
                                {categoryName}
                            </Typography>
                            <Box sx={{ border: '1px solid #e0e4e8', borderRadius: '8px', overflow: 'hidden' }}>
                                <table className="table table-sm mb-0" style={{ fontFamily: '"Source Sans Pro", "Poppins", sans-serif' }}>
                                    <thead style={{ backgroundColor: '#f4f6f8' }}>
                                        <tr>
                                            <th style={{ width: '60px', padding: '6px 4px', verticalAlign: 'middle', borderBottom: '1px solid #e0e4e8', textAlign: 'left', fontSize: '12px', color: '#4a5568', fontWeight: 600 }}>Select</th>
                                            <th style={{ width: '60px', padding: '6px 4px', verticalAlign: 'middle', borderBottom: '1px solid #e0e4e8', textAlign: 'left', fontSize: '12px', color: '#4a5568', fontWeight: 600 }}>Sr. No.</th>
                                            <th style={{ padding: '6px 4px', verticalAlign: 'middle', borderBottom: '1px solid #e0e4e8', textAlign: 'left', fontSize: '12px', color: '#4a5568', fontWeight: 600 }}>Description</th>
                                            <th style={{ width: '100px', padding: '6px 4px', verticalAlign: 'middle', borderBottom: '1px solid #e0e4e8', textAlign: 'left', fontSize: '12px', color: '#4a5568', fontWeight: 600 }}>Diameter</th>
                                            <th style={{ width: '100px', padding: '6px 4px', verticalAlign: 'middle', borderBottom: '1px solid #e0e4e8', textAlign: 'left', fontSize: '12px', color: '#4a5568', fontWeight: 600 }}>Length</th>
                                            <th style={{ width: '150px', padding: '6px 4px', verticalAlign: 'middle', borderBottom: '1px solid #e0e4e8', textAlign: 'left', fontSize: '12px', color: '#4a5568', fontWeight: 600 }}>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {localParts.map((part, rowIdx) => {
                                            if (part.category !== categoryName) return null;
                                            const isDisabled = !part.isChecked; 
                                            
                                            return (
                                                <tr key={rowIdx} style={{ opacity: isDisabled ? 0.7 : 1, transition: 'opacity 0.2s', borderBottom: '1px solid #f0f0f0', backgroundColor: isDisabled ? '#fafafa' : '#fff' }}>
                                                    <td style={{ padding: '2px 4px', verticalAlign: 'middle', textAlign: 'left' }}>
                                                        <Checkbox 
                                                            size="small"
                                                            checked={part.isChecked} 
                                                            icon={<CheckCircleSharpIcon sx={{ color: '#d0d0d0', fontSize: 18 }} />} 
                                                            checkedIcon={<CheckCircleSharpIcon sx={{ color: '#0070c0', fontSize: 18 }} />}
                                                            onChange={(e) => {
                                                                handleLocalRowChange(rowIdx, 'isChecked', e.target.checked);
                                                                pushRowChange(rowIdx, 'isChecked', e.target.checked);
                                                            }} 
                                                            sx={{ padding: '2px' }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '2px 4px', fontSize: '13px', verticalAlign: 'middle', textAlign: 'left', color: '#555' }}>{rowIdx + 1}</td>
                                                    <td style={{ padding: '2px 4px', verticalAlign: 'middle', textAlign: 'left' }}>
                                                        {part.isEditable ? (
                                                            <TextField 
                                                                size="small" 
                                                                fullWidth
                                                                value={part.name} 
                                                                disabled={isDisabled}
                                                                placeholder="Enter special part name"
                                                                onChange={(e) => handleLocalRowChange(rowIdx, 'name', e.target.value)} 
                                                                onBlur={(e) => pushRowChange(rowIdx, 'name', e.target.value)}
                                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                                                InputProps={{ style: { fontSize: '13px', fontFamily: '"Source Sans Pro", sans-serif' } }}
                                                                inputProps={{ style: { padding: '4px 6px' } }}
                                                            />
                                                        ) : (
                                                            <span style={{ fontSize: '13px', color: '#333', fontWeight: 500 }}>{part.name}</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '2px 4px', verticalAlign: 'middle', textAlign: 'left' }}>
                                                        <TextField size="small" fullWidth value={part.diameter} disabled={isDisabled} onChange={(e) => handleLocalRowChange(rowIdx, 'diameter', e.target.value)} onBlur={(e) => pushRowChange(rowIdx, 'diameter', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }} InputProps={{ style: { fontSize: '13px', fontFamily: '"Source Sans Pro", sans-serif' } }} inputProps={{ style: { padding: '4px 6px', textAlign: 'left' } }} />
                                                    </td>
                                                    <td style={{ padding: '2px 4px', verticalAlign: 'middle', textAlign: 'left' }}>
                                                        <TextField size="small" fullWidth value={part.length} disabled={isDisabled} onChange={(e) => handleLocalRowChange(rowIdx, 'length', e.target.value)} onBlur={(e) => pushRowChange(rowIdx, 'length', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }} InputProps={{ style: { fontSize: '13px', fontFamily: '"Source Sans Pro", sans-serif' } }} inputProps={{ style: { padding: '4px 6px', textAlign: 'left' } }} />
                                                    </td>
                                                    <td style={{ padding: '2px 4px', verticalAlign: 'middle', textAlign: 'left' }}>
                                                        <TextField size="small" fullWidth value={part.remarks} disabled={isDisabled} onChange={(e) => handleLocalRowChange(rowIdx, 'remarks', e.target.value)} onBlur={(e) => pushRowChange(rowIdx, 'remarks', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }} InputProps={{ style: { fontSize: '13px', fontFamily: '"Source Sans Pro", sans-serif' } }} inputProps={{ style: { padding: '4px 6px' } }} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Box>
                        </Box>
                    ))
                )}
            </div>

            {/* Schematic Image Section */}
            <Box mt={2} mb={4} sx={{ paddingLeft: '15px', paddingRight: '15px' }}>
                <div className="bom-table-title" style={{ marginBottom: '16px', fontSize: '16px' }}>
                    Schematic Image
                </div>
                
                <Box 
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    sx={{ 
                        border: '2px dashed #d9d9d9', 
                        borderRadius: '12px', 
                        padding: localImageUrl ? '16px' : '40px 20px', 
                        background: localImageUrl ? '#fff' : '#fafafa', 
                        textAlign: 'center',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '200px',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            borderColor: '#2d3d42',
                            background: localImageUrl ? '#fff' : '#f4f6f8'
                        }
                    }}
                >
                    {localImageUrl ? (
                        <>
                            <Box 
                                sx={{ position: 'relative', display: 'inline-block', width: '100%', cursor: 'pointer', '&:hover .fullscreen-icon': { opacity: 1 } }} 
                                onClick={handleOpenModal}
                                title="Click to view full screen"
                            >
                                <img 
                                    src={localImageUrl} 
                                    alt="Schematic" 
                                    style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '400px', 
                                        objectFit: 'contain', 
                                        borderRadius: '8px', 
                                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                        display: 'block',
                                        margin: '0 auto',
                                        transition: 'transform 0.2s'
                                    }} 
                                />
                                <Box 
                                    className="fullscreen-icon"
                                    sx={{ 
                                        position: 'absolute', 
                                        top: 10, 
                                        right: 10, 
                                        backgroundColor: 'rgba(255,255,255,0.9)', 
                                        borderRadius: '50%', 
                                        padding: '4px',
                                        display: 'flex',
                                        opacity: 0,
                                        transition: 'opacity 0.2s ease',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <FullscreenIcon sx={{ color: '#2d3d42' }} />
                                </Box>
                            </Box>
                            <Button 
                                variant="outlined" 
                                component="label" 
                                disabled={isUploading}
                                startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                                sx={{ 
                                    mt: 3, 
                                    borderColor: '#e0e0e0', 
                                    color: '#4a5568', 
                                    textTransform: 'none', 
                                    fontWeight: 500,
                                    borderRadius: '6px',
                                    padding: '6px 20px',
                                    '&:hover': { borderColor: '#2d3d42', backgroundColor: 'rgba(45, 61, 66, 0.04)', color: '#2d3d42' } 
                                }}
                            >
                                Replace Image
                                <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                            </Button>
                        </>
                    ) : (
                        <>
                            <ImageIcon sx={{ fontSize: 48, color: '#c0c0c0', mb: 2 }} />
                            <Typography variant="body1" sx={{ color: '#4a5568', mb: 1, fontWeight: 500 }}>
                                Drag and drop an image, or click to upload
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#888', mb: 3 }}>
                                Supports JPG, PNG, WEBP (Max 5MB)
                            </Typography>
                            <Button 
                                // variant="contained" 
                                component="label" 
                                disabled={isUploading}
                                startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                                sx={{ textTransform: 'none', color: '#2d3d42' }}

                            >
                                Upload Image
                                <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {/* Bottom Checkboxes */}
            <Box mt={4} pt={3} sx={{ borderTop: '1px solid #eaeaea', paddingLeft: '15px' }} display="flex" flexWrap="wrap" gap={4}>
                <FormControlLabel
                    control={
                        <Checkbox 
                            name="allRequirementsFulfilled" 
                            checked={localAllRequirementsFulfilled} 
                            onChange={handleCheckboxChange} 
                            icon={<CheckCircleSharpIcon sx={{ color: '#d0d0d0', fontSize: 20 }} />} 
                            checkedIcon={<CheckCircleSharpIcon sx={{ color: '#0070c0', fontSize: 20 }} />}
                        />
                    }
                    label={<Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#2d3d42' }}>All Requirements Fulfilled</Typography>}
                />
                <FormControlLabel
                    control={
                        <Checkbox 
                            name="isComplete" 
                            checked={localIsComplete} 
                            onChange={handleCheckboxChange} 
                            icon={<CheckCircleSharpIcon sx={{ color: '#d0d0d0', fontSize: 20 }} />} 
                            checkedIcon={<CheckCircleSharpIcon sx={{ color: '#0070c0', fontSize: 20 }} />}
                        />
                    }
                    label={<Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#2d3d42' }}>Feasibility Study Complete</Typography>}
                />
            </Box>

            {/* Image Preview Dialog */}
            <Dialog
                open={isImageModalOpen}
                onClose={handleCloseModal}
                maxWidth="lg"
                fullWidth
                sx={{ zIndex: 999999 }}
                PaperProps={{
                    sx: {
                        background: 'transparent',
                        boxShadow: 'none',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }
                }}
            >
                <Box sx={{ position: 'relative', background: '#fff', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '90vh', minWidth: '50vw' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #eee', background: '#f8f9fa', flexWrap: 'nowrap' }}>
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
                            <Typography variant="h6" sx={{ color: '#2d3d42', fontWeight: 600, fontSize: '16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Schematic Preview</Typography>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
                            <IconButton onClick={handleZoomOut} size="small" sx={{ color: '#555', border: '1px solid #ddd', borderRadius: '4px' }} disabled={zoomLevel <= 0.5}>
                                <ZoomOutIcon />
                            </IconButton>
                            <Typography sx={{ display: 'flex', alignItems: 'center', px: 1, color: '#666', fontSize: '14px', minWidth: '48px', justifyContent: 'center' }}>{Math.round(zoomLevel * 100)}%</Typography>
                            <IconButton onClick={handleZoomIn} size="small" sx={{ color: '#555', border: '1px solid #ddd', borderRadius: '4px' }} disabled={zoomLevel >= 5}>
                                <ZoomInIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton onClick={handleCloseModal} size="small" sx={{ color: '#555' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    <DialogContent sx={{ p: 0, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: '#e0e0e0' }}>
                        <img 
                            src={localImageUrl} 
                            alt="Schematic Preview" 
                            style={{ 
                                transform: `scale(${zoomLevel})`, 
                                transition: 'transform 0.2s ease-in-out',
                                transformOrigin: 'center center',
                                maxWidth: '100%',
                                objectFit: 'contain'
                            }} 
                        />
                    </DialogContent>
                </Box>
            </Dialog>
        </div>
    );
};

export default FeasibilityStudyCard;
